import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Flex, Form, Spin } from "antd";

import { useAppDispatch, useAppSelector } from "../../../../../../../../store/config";
import { IUploadFileParams } from "../../../../../../../../interfaces/files/files";
import { IWorkout } from "../../../../../../../../interfaces/store/workout-interface";
import { ICreateWorkoutInProgramPayload, IUpdateWorkoutInProgramPayload } from "../../../../../../../../interfaces/store/training-program-interface";
import { AbsoluteButton55, RelativeFlex, StyledColumnWrapper, StyledForm, StyledLayout } from "../../../../../../../../components/common/styles";
import { IMAGE_BASE_URL } from "../../../../../../../../common/config";
import { DropdownField, ImageInputField, TextAreaField, TextInputField } from "../../../../../../../../components/inputs";
import { createWorkoutInProgramStart, updateWorkoutInProgramStart } from "../../../../../../../../store/features/workout/workout-slice";
import { blobToBase64 } from "../../../../../../../../utils/utils";

type TrainingProgramWorkoutDetailsProps = {
  isEditable: boolean;
  setIsEditable: (val: boolean) => void;
  workout: IWorkout | null;
  loading: boolean;
  isNew: boolean;
  openNotification: () => void;
  type: "video" | "exercises";
};

interface WorkoutValues {
  name: string;
  description: string;
  trainer: string;
  tags: string[];
  trainingType: string;
}

const TrainingProgramWorkoutDetails = ({ isEditable, setIsEditable, workout, loading, isNew, openNotification, type }: TrainingProgramWorkoutDetailsProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [imageData, setImageData] = useState<IUploadFileParams | null>(null);
  const [largeImageData, setLargeImageData] = useState<IUploadFileParams | null>(null);

  const { trainingProgramId } = useParams<{ trainingProgramId: string }>();
  const { id: trainerId } = useParams<{ id: string }>();
  const { workoutId } = useParams<{ workoutId: string }>();

  const { trainingProgram, loading: trainingProgramLoading, error } = useAppSelector((state) => state.trainingProgram);

  useEffect(() => {
    if (error) {
      openNotification();
    }
  }, [error]);

  useEffect(() => {
    if (workout && trainingProgram) {
      const index = trainingProgram.workouts.findIndex((w) => (w?._id || w) === workout._id);
      let day, week;
      if (index !== -1) {
        day = index % trainingProgram.timesPerWeek;
        week = (index - day) / trainingProgram.timesPerWeek;
      }

      form.setFieldsValue({
        name: workout.name,
        trainer: workout.trainer,
        description: workout.description,
        day: day !== undefined ? day + "" : "-",
        week: week !== undefined ? week + "" : "-",
      });
    }
  }, [workout, trainingProgram]);

  const upsertWorkout = async (values: any) => {
    if (!isEditable) {
      setIsEditable(true);
      return;
    }

    if (!trainingProgram) return;

    const workout = {
      name: values.name,
      description: values.description,
      trainer: trainerId || "",
      tags: [] as string[],
      trainingType: trainingProgram.trainingType,
    };

    const index = parseInt(values.week) * trainingProgram.timesPerWeek + parseInt(values.day);

    const imageDataPayload = imageData
      ? {
          ...imageData,
          binaryFile: await blobToBase64(imageData.binaryFile as Blob),
          fileType: "image",
        }
      : null;

    const largeImageDataPayload = largeImageData
      ? {
          ...largeImageData,
          binaryFile: await blobToBase64(largeImageData.binaryFile as Blob),
          fileType: "image",
        }
      : null;

    const upsertWorkoutBody = {
      workout: { ...workout, ...(isNew ? { type } : {}) },
      trainingProgramId,
      index: isNew ? index : trainingProgram.workouts.findIndex((w) => (w?._id || w) === workoutId) === index ? -1 : index,
      ...(workoutId && !isNew && { workoutId }),
      ...(imageDataPayload && { imageData: imageDataPayload }),
      ...(largeImageDataPayload && { largeImageData: largeImageDataPayload }),
    };

    dispatch(isNew ? createWorkoutInProgramStart(upsertWorkoutBody as ICreateWorkoutInProgramPayload) : updateWorkoutInProgramStart(upsertWorkoutBody as IUpdateWorkoutInProgramPayload));
  };

  return (
    <>
      <RelativeFlex>
        <AbsoluteButton55 onClick={() => form.submit()}>{isEditable ? t("Save") : t("Edit")}</AbsoluteButton55>
      </RelativeFlex>

      <Spin spinning={loading || trainingProgramLoading}>
        <StyledForm form={form} onFinish={upsertWorkout} validateTrigger="onSubmit">
          <StyledLayout>
            <StyledColumnWrapper>
              <TextInputField name="name" label={t("Workout Name")} isEditable={isEditable} rules={[{ required: true, message: t("Required") }]} />
              <Flex gap={20}>
                <DropdownField
                  label={t("Week In Program")}
                  options={Array.from({ length: trainingProgram?.numberOfWeeks || 0 }, (_, i) => i + 1).map((week, index) => ({
                    label: `${t("Week")} ${index + 1}`,
                    value: index + "",
                  }))}
                  isEditable={isEditable}
                  name="week"
                  rules={[{ required: true, message: t("Required") }]}
                />
                <DropdownField
                  label={t("Day In Program")}
                  options={Array.from({ length: trainingProgram?.timesPerWeek || 0 }, (_, i) => i + 1).map((_, index) => ({
                    label: ` ${index + 1}`,
                    value: index + "",
                  }))}
                  isEditable={isEditable}
                  name="day"
                  rules={[{ required: true, message: t("Required") }]}
                />
              </Flex>
              <>
                <TextAreaField name="description" label={t("Description")} isEditable={isEditable} />
              </>
            </StyledColumnWrapper>
            <StyledColumnWrapper>
              <ImageInputField
                label={t("Workout Outer Cover Image")}
                isEditable={isEditable}
                value={workout?.outerCover ? IMAGE_BASE_URL + "/" + workout.outerCover : undefined}
                onChange={setImageData}
                aspect={147 / 187}
              />
              <ImageInputField
                label={t("Workout Inner Cover Image")}
                isEditable={isEditable}
                value={workout?.innerCover ? IMAGE_BASE_URL + "/" + workout.innerCover : undefined}
                onChange={setLargeImageData}
                aspect={390 / 253}
              />
            </StyledColumnWrapper>
          </StyledLayout>
        </StyledForm>
      </Spin>
    </>
  );
};

export default TrainingProgramWorkoutDetails;
